import { useEffect, useRef, useState, useCallback } from "react";
import { useAuthStore } from "@/stores/authStore";

export type WebSocketMessageType =
  | "connected"
  | "generation_started"
  | "generation_progress"
  | "generation_agent_update"
  | "generation_complete"
  | "generation_failed"
  | "pong"
  | "error";

export interface WebSocketMessage {
  type: WebSocketMessageType;
  data: Record<string, unknown>;
}

export interface GenerationProgress {
  agent: string;
  percent: number;
  status: string;
  message: string;
}

export interface GenerationComplete {
  title: string;
  word_count: number;
  seo_score: number;
  geo_score: number;
  human_score: number;
  cost: number;
  generation_time: number | null;
}

export interface GenerationFailed {
  error: string;
}

export interface GenerationStarted {
  article_id: string;
  topic: string;
  status: string;
}

export interface AgentUpdate {
  article_id: string;
  agent: string;
  agent_status: string;
  agent_percent: number;
  message: string;
}

interface UseWebSocketOptions {
  articleId: string;
  onStarted?: (data: GenerationStarted) => void;
  onProgress?: (data: GenerationProgress) => void;
  onAgentUpdate?: (data: AgentUpdate) => void;
  onComplete?: (data: GenerationComplete) => void;
  onFailed?: (data: GenerationFailed) => void;
  onConnected?: () => void;
  autoConnect?: boolean;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  lastMessage: WebSocketMessage | null;
  connect: () => void;
  disconnect: () => void;
  sendPing: () => void;
}

export function useWebSocket({
  articleId,
  onStarted,
  onProgress,
  onAgentUpdate,
  onComplete,
  onFailed,
  onConnected,
  autoConnect = true,
}: UseWebSocketOptions): UseWebSocketReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);

  const { accessToken } = useAuthStore();

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    cleanup();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [cleanup]);

  const connect = useCallback(() => {
    if (!accessToken || !articleId) {
      return;
    }

    // Clean up existing connection
    disconnect();

    // Use same origin WebSocket (proxied by Vite in dev).
    // Token is sent in the first message, not the URL, to keep it out of logs.
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl =
      import.meta.env.VITE_WS_URL || `${protocol}//${window.location.host}`;
    const url = `${wsUrl}/ws/articles/${articleId}`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      // Authenticate via first-message handshake
      ws.send(JSON.stringify({ type: "auth", token: accessToken }));
      setIsConnected(true);

      // Start ping interval to keep connection alive
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        setLastMessage(message);

        switch (message.type) {
          case "connected":
            onConnected?.();
            break;
          case "generation_started":
            onStarted?.(message.data as unknown as GenerationStarted);
            break;
          case "generation_progress":
            onProgress?.(message.data as unknown as GenerationProgress);
            break;
          case "generation_agent_update":
            onAgentUpdate?.(message.data as unknown as AgentUpdate);
            break;
          case "generation_complete":
            onComplete?.(message.data as unknown as GenerationComplete);
            break;
          case "generation_failed":
            onFailed?.(message.data as unknown as GenerationFailed);
            break;
        }
      } catch (err) {
        console.error("Failed to parse WebSocket message:", err);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    ws.onclose = (event) => {
      setIsConnected(false);
      cleanup();

      // Reconnect on unexpected close (not user-initiated)
      if (event.code !== 1000 && event.code !== 1001) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 3000);
      }
    };
  }, [
    accessToken,
    articleId,
    cleanup,
    disconnect,
    onConnected,
    onStarted,
    onProgress,
    onAgentUpdate,
    onComplete,
    onFailed,
  ]);

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "ping" }));
    }
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect && accessToken && articleId) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, accessToken, articleId, connect, disconnect]);

  return {
    isConnected,
    lastMessage,
    connect,
    disconnect,
    sendPing,
  };
}
